import type { BaseHeaderResponse } from '~/api/types/graphqlClient';
import type { IWSCachedQueryConfig } from './type';

import { BaseHeaderXCache } from '~/api/types/graphqlClient';
import { getCachedData, isLoadingData } from './helpers';
import { useWebsocketContext } from '../contexts';
import { useMemo } from 'react';

export const useBaseCachedQuery = <
  TData,
  TResponse extends { headers: BaseHeaderResponse },
  TVariables = Record<string, unknown>,
>(
  config: IWSCachedQueryConfig<TData, TResponse, TVariables>,
) => {
  const { isConnected } = useWebsocketContext();

  const { cache, upsertCache, parameterKey, enabled, getResponseData, variables } = config;
  const { onSuccess: onSuccessCallback, onError, onSettled } = config;

  const data = useMemo(() => getCachedData<TData>(parameterKey, cache), [cache, parameterKey]);
  const isLoading = useMemo(() => isLoadingData(parameterKey, cache), [cache, parameterKey]);

  const handleSuccess = (response: TResponse) => {
    const xCache = response?.headers['x-cache'] || config.xCache;
    const xRequestId = response?.headers['x-request-id'];

    if (xCache === BaseHeaderXCache.HIT) {
      upsertCache(parameterKey, {
        requestId: xRequestId ?? '',
        data: getResponseData(response),
      });
    } else {
      upsertCache(parameterKey, { requestId: xRequestId ?? '' });
    }

    onSuccessCallback?.(response);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { refetch } =
    (config.useQuery(variables, {
      enabled: enabled && isConnected,
      retry: 0,
      onSuccess: handleSuccess,
      onError,
      // onSettled,
    }) as any) ?? {};

  return { data, isLoading, refetch: refetch as (() => unknown) | undefined };
};
